import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowLeft, CheckCircle2, Clock } from "lucide-react";
import { Badge } from "@tellescope/ui";
import { Button } from "@tellescope/design-system";
import { formatDateTime } from "@tellescope/utils";
import type { FormResponse } from "@tellescope/types-models";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { renderAnswer } from "../../../shared/forms";
import { PendingFormContent } from "../../components/blocks/NativeFormResponseDialog/PendingFormContent";
import { useIframeToken } from "../../hooks/useIframeToken";
import { useOrganization } from "../../../shared/store/organizationStore";
import { useIsNewFormsExperienceEnabled } from "../../../shared/store/portalSchemaStore";
import { buildFormCompletionURL, buildFormResponseRoute } from "../../../config/portal";

interface FormAnswerProps {
  title: string;
  answer: NonNullable<FormResponse["responses"]>[number]["answer"];
  answerIsHTML?: boolean;
}

const FormAnswer = ({ title, answer, answerIsHTML }: FormAnswerProps) => {
  const renderedAnswer = renderAnswer(answer, answerIsHTML);
  if (!renderedAnswer) return null;

  return (
    <div className="border-b border-border pb-3 last:border-b-0">
      <div className="text-sm font-medium text-muted-foreground mb-1">{title}</div>
      <div className="text-sm text-foreground">{renderedAnswer}</div>
    </div>
  );
};

/**
 * Legacy form response view page that handles direct form response URLs.
 * Maps to old portal's /documents/form-responses/:id route.
 *
 * URL params:
 * - id: the form response ID
 */
export const FormResponseViewPage = () => {
  const { t } = useTranslation("forms");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const organization = useOrganization();
  const isNewFormsExperience = useIsNewFormsExperienceEnabled();

  const {
    data: formResponse,
    isLoading: isLoadingResponse,
    error: fetchError,
  } = useTellescopeQuery({
    model: "form_responses",
    method: "getOne",
    request: id ?? "",
    enabled: !!id,
  });

  const isSubmitted = !!formResponse?.submittedAt;
  const hasDraft = !!formResponse?.draftSavedAt;
  const isPending = formResponse && !isSubmitted;
  const usesNewFormsExperience = isNewFormsExperience(formResponse?.formId);

  const { token: iframeToken, isLoading: isLoadingToken } = useIframeToken({
    enabled: !usesNewFormsExperience && !!formResponse && !formResponse.submittedAt,
  });

  const anyShared = formResponse?.responses?.some((r) => r.sharedWithEnduser === true);
  const visibleResponses = formResponse?.responses?.filter(
    (response) => !anyShared || response.sharedWithEnduser === true
  );

  const formCompletionURL =
    !usesNewFormsExperience && isPending && formResponse && organization && iframeToken && formResponse.accessCode
      ? buildFormCompletionURL({
          organization: { subdomain: organization.subdomain },
          formId: formResponse.formId,
          accessCode: formResponse.accessCode,
          authToken: iframeToken,
          automationStepId: formResponse.automationStepId,
          hideBg: true,
          startAtNext: true,
        })
      : null;

  if (isLoadingResponse) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">{t("Loading form...")}</p>
        </div>
      </div>
    );
  }

  if (fetchError || !formResponse) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-sm font-medium text-destructive mb-1">{t("Unable to load form")}</p>
          <p className="text-xs text-muted-foreground mb-4">{fetchError?.message ?? t("Form not found")}</p>
          <Link to="/documents">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("Go Back")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Pending form — show the wizard (native) or the iframe (legacy) for completion
  if (isPending) {
    if (!usesNewFormsExperience && isLoadingToken) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center text-muted-foreground">
            <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
            <p className="text-sm">{t("Loading form...")}</p>
          </div>
        </div>
      );
    }

    const canCompleteForm = usesNewFormsExperience ? !!formResponse.accessCode : !!formCompletionURL;

    if (!canCompleteForm) {
      // Missing data to render the form (no access code, or token fetch failed)
      return (
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <Link to="/documents">
              <Button variant="ghost" size="sm" iconOnly className="h-8 w-8 p-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <h1 className="text-2xl font-bold truncate">{formResponse.formTitle}</h1>
          </div>

          <div className="bg-background rounded-lg border p-8">
            <div className="flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Clock className="h-8 w-8 mx-auto mb-2 text-amber-600" />
                <p className="text-sm font-medium mb-1">{hasDraft ? t("Draft saved") : t("Awaiting completion")}</p>
                <p className="text-xs">
                  {hasDraft && formResponse.draftSavedAt
                    ? formatDateTime(formResponse.draftSavedAt)
                    : t("This form is ready to be completed")}
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="h-screen flex flex-col">
        <div className="flex items-center gap-3 px-4 py-3 border-b flex-shrink-0">
          <Link to="/documents">
            <Button variant="ghost" size="sm" iconOnly className="h-8 w-8 p-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-base font-medium truncate">{formResponse.formTitle}</h1>
        </div>

        {usesNewFormsExperience ? (
          <div className="flex-1 overflow-hidden flex flex-col items-center">
            <div className="flex flex-col w-full max-w-[650px] flex-1 min-h-0">
              <PendingFormContent
                key={formResponse.id}
                formResponse={formResponse}
                onComplete={() => navigate("/documents")}
                continueToFormInApp={(prepared) => {
                  navigate(buildFormResponseRoute(prepared.id), { replace: true });
                  return true;
                }}
              />
            </div>
          </div>
        ) : (
          <iframe
            src={formCompletionURL ?? undefined}
            className="flex-1 w-full border-0"
            title={formResponse.formTitle}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        )}
      </div>
    );
  }

  // Submitted form — show responses
  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      {/* Header with back button */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/documents">
          <Button variant="ghost" size="sm" iconOnly className="h-8 w-8 p-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold truncate">{formResponse.formTitle}</h1>
      </div>

      {/* Content */}
      <div className="bg-background rounded-lg border overflow-hidden">
        {/* Metadata section */}
        <div className="px-4 py-3 border-b bg-muted/30">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span>{t("Submitted {{timestamp}}", { timestamp: formatDateTime(formResponse.submittedAt!) })}</span>
          </div>

          {/* Show scores if available */}
          {formResponse.scores && formResponse.scores.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formResponse.scores.map((score, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {score.title}: {score.value}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Responses */}
        <div className="p-4">
          {!visibleResponses || visibleResponses.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <p className="text-sm">{t("No responses recorded")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {visibleResponses.map((response) => (
                <FormAnswer
                  key={response.fieldId}
                  title={response.fieldTitle}
                  answer={response.answer}
                  answerIsHTML={response.answerIsHTML}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
